import { useCallback, useEffect, useRef, useState } from "react";
import { useAirframeService } from "../../../../services/airframeService";
import { useComponentService } from "../../../../services/componentService";
import { useSnackbar } from "../../../../contexts/snackbar-context";
import { useMsal } from "@azure/msal-react";

import { Component } from "../../../../types/component";
import {
  Airframe,
  CreateAirframeRequest,
  UpdateAirframeRequest,
} from "../../../../types/airframe";
import { filterOrphanedInstalledComponents } from "../../../../utils/componentWarehouse";
import { getComponentTypeName } from "../../../../utils/componentTypeUtils";
import { parseComponentCustomMetadata } from "../../../../utils/landingGearAttachUtils";

const parseDate = (date: any): string => {
  if (!date) return "";

  if (typeof date === "string") {
    const iso = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return iso[0];

    const dmy = date.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;

    return date.split("T")[0] || date.split(" ")[0];
  }

  if (date instanceof Date) {
    return date.toISOString().split("T")[0];
  }

  return "";
};

export function useAirframes(loadComponents = true): {
  airframes: Airframe[];
  components: Component[];
  loading: boolean;
  saveAirframe: (
    request: CreateAirframeRequest | UpdateAirframeRequest,
    id?: string
  ) => Promise<void>;
  deletePkg: (pkg: Airframe) => Promise<void>;
  reload: () => Promise<void>;
  confirmDelete: { open: boolean; airframe: Airframe | null };
  setConfirmDelete: (state: { open: boolean; airframe: Airframe | null }) => void;
  handleConfirmDelete: () => Promise<void>;
  isDeletingAirframe: boolean;
} {
  const [airframes, setAirframes] = useState<Airframe[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    airframe: Airframe | null;
  }>({ open: false, airframe: null });

  const [isDeletingAirframe, setIsDeletingAirframe] = useState(false);

  const initialLoadDone = useRef(false);

  const { showError, showSuccess } = useSnackbar();
  const { accounts } = useMsal();

  const { getAirframes, updateAirframe, createAirframe, deleteAirframe } =
    useAirframeService();

  const { getComponents } = useComponentService();

  /*
  -----------------------------------
  LOAD DATA
  -----------------------------------
  */

  const loadData = useCallback(async () => {
    setLoading(true);
    let mappedAirframes: Airframe[] = [];

    /* -----------------------------
       AIRFRAMES
    ----------------------------- */

    try {
      const res = await getAirframes() as any;

      const list =
        res?.data?.data ??
        res?.data ??
        (Array.isArray(res) ? res : []);

      const mapped: Airframe[] = list.map((a: any) => ({
        id: a.Id ?? a.id,

        manufacturer: a.Manufacturer ?? a.manufacturer ?? "",
        model: a.Model ?? a.model ?? "",
        series: a.Series ?? a.series ?? undefined,
        commonName: a.CommonName ?? a.commonName ?? undefined,

        tailNumber: a.TailNumber ?? a.tailNumber ?? "",
        serialNumber: a.SerialNumber ?? a.serialNumber ?? "",

        dateManufactured: parseDate(
          a.DateManufactured ?? a.dateManufactured
        ),

        totalFlightHours:
          a.TotalFlightHours ?? a.totalFlightHours ?? 0,

        totalFlightCycles:
          a.TotalFlightCycles ?? a.totalFlightCycles ?? 0,

        lastHMVDate: parseDate(
          a.LastHMVDate ?? a.lastHMVDate
        ),

        lastHMVType:
          a.LastHMVType ?? a.lastHMVType ?? null,

        operationalCategory:
          a.OperationalCategory ?? a.operationalCategory ?? "",

        landingCategory:
          a.LandingCategory ?? a.landingCategory ?? "",

        etopsCategory:
          a.EtopsCategory ?? a.etopsCategory ?? "",

        customFields:
          a.CustomFields ?? a.customFields ?? null,

        ownershipStatus:
          a.OwnershipStatus ?? a.ownershipStatus ?? null,
        ownershipStatusLabel:
          a.OwnershipStatusLabel ?? a.ownershipStatusLabel ?? null,
        operatorId: a.OperatorId ?? a.operatorId ?? null,
        operatorName: a.OperatorName ?? a.operatorName ?? null,
        owner: a.Owner ?? a.owner ?? null,

        slots: (a.Slots ?? a.slots ?? []).map((s: any) => {
          const rawType = s.ComponentType ?? s.componentType ?? "";
          const componentType =
            getComponentTypeName(rawType) ?? String(rawType ?? "");

          const installed = s.InstalledComponent ?? s.installedComponent ?? null;

          return {
            id: s.Id ?? s.id,
            airframeId: s.AirframeId ?? s.airframeId,
            componentType,
            slotNumber: s.SlotNumber ?? s.slotNumber ?? 0,
            locationCode: s.LocationCode ?? s.locationCode ?? "",
            isActive: s.IsActive ?? s.isActive ?? true,
            createdDate: s.CreatedDate ?? s.createdDate ?? "",
            modifiedDate: s.ModifiedDate ?? s.modifiedDate ?? null,
            installedComponent: installed
              ? {
                  id: installed.Id ?? installed.id,
                  airframeId: installed.AirframeId ?? installed.airframeId,
                  slotId: installed.SlotId ?? installed.slotId,
                  componentId: installed.ComponentId ?? installed.componentId,
                  installDate: installed.InstallDate ?? installed.installDate,
                  removedDate: installed.RemovedDate ?? installed.removedDate ?? null,
                  installRemarks: installed.InstallRemarks ?? installed.installRemarks ?? null,
                  hoursAtInstall: installed.HoursAtInstall ?? installed.hoursAtInstall ?? 0,
                  cyclesAtInstall: installed.CyclesAtInstall ?? installed.cyclesAtInstall ?? 0,
                  createdDate: installed.CreatedDate ?? installed.createdDate ?? "",
                  modifiedDate: installed.ModifiedDate ?? installed.modifiedDate ?? null,
                }
              : null,
          };
        }),

        isDeleted:
          a.IsDeleted ?? a.isDeleted ?? false,

        createdDate: parseDate(
          a.CreatedAt ??
            a.createdAt ??
            a.CreatedDate ??
            a.createdDate
        ),

        modifiedDate: parseDate(
          a.UpdatedAt ??
            a.updatedAt ??
            a.ModifiedDate ??
            a.modifiedDate
        ),

        createdBy:
          a.CreatedBy ?? a.createdBy ?? null,

        updatedBy:
          a.UpdatedBy ?? a.updatedBy ?? null,
      }));

      mappedAirframes = mapped;
      setAirframes(mapped);
    } catch (err) {
      console.error("Airframe fetch failed", err);
      showError("Failed to load airframes");
      setAirframes([]);
    }

    /* -----------------------------
       COMPONENTS
    ----------------------------- */

    if (loadComponents) {
      try {
        const res = await getComponents();

        const list: Component[] =
          res?.data ?? (Array.isArray(res) ? res : []);

        const mappedComponents = list.map((component: any) => ({
          ...component,
          type:
            getComponentTypeName(component?.componentType ?? component?.ComponentType) ??
            component?.type,
          customMetadata: parseComponentCustomMetadata({
            customMetadata: component?.customMetadata,
            customFields: component?.customFields ?? component?.CustomFields,
          }),
        }));

        setComponents(filterOrphanedInstalledComponents(mappedComponents, mappedAirframes));
      } catch (err) {
        console.warn("Component fetch failed", err);

        setComponents([]);
      }
    } else {
      setComponents([]);
    }

    setLoading(false);
  }, [getAirframes, getComponents, loadComponents, showError]);

  //save airframe

  const saveAirframe = useCallback(
    async (
      request: CreateAirframeRequest | UpdateAirframeRequest,
      id?: string
    ) => {
      try {
        const user =
          accounts?.[0]?.name?.trim() ||
          accounts?.[0]?.username?.trim() ||
          "system";

        if (id) {
          const updateReq: UpdateAirframeRequest = {
            ...request,
            updatedBy: user,
          };

          await updateAirframe(id, updateReq);
        } else {
          const createReq: CreateAirframeRequest = {
            ...(request as CreateAirframeRequest),
            createdBy: user,
          };

          await createAirframe(createReq);
        }

        showSuccess(
          `Airframe ${id ? "updated" : "created"} successfully`
        );

        await loadData();
      } catch (err) {
        console.error(err);
        const fallback = `Failed to ${id ? "update" : "create"} airframe`;
        const raw =
          err instanceof Error && err.message.trim().length > 0
            ? err.message.trim()
            : "";
        const message =
          raw.replace(/\s*CorrelationId=[^\s]+$/i, "").trim() || fallback;
        showError(message);
      }
    },
    [
      accounts,
      updateAirframe,
      createAirframe,
      showSuccess,
      showError,
      loadData,
    ]
  );

  /*
  -----------------------------------
  DELETE AIRFRAME
  -----------------------------------
  */

  const deletePkg = async (pkg: Airframe) => {
    if (!pkg.id) return;

    setConfirmDelete({
      open: true,
      airframe: pkg,
    });
  };

  const handleConfirmDelete = async () => {
    const pkg = confirmDelete.airframe;
    if (!pkg?.id) return;

    try {
      setIsDeletingAirframe(true);
      await deleteAirframe(pkg.id);

      showSuccess("Airframe deleted");

      setConfirmDelete({
        open: false,
        airframe: null,
      });

      await loadData();
    } catch {
      showError("Failed to delete airframe");

      setConfirmDelete({
        open: false,
        airframe: null,
      });
    } finally {
      setIsDeletingAirframe(false);
    }
  };

  
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadData();
    }
  }, [loadData]);

  return {
    airframes,
    components,
    loading,
    saveAirframe,
    deletePkg,
    reload: loadData,
    confirmDelete,
    setConfirmDelete,
    handleConfirmDelete,
    isDeletingAirframe,
  };
}